import { Descriptions, Progress, Table, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { getPredictionById } from "../../apis/prediction";
import { useLoader } from "../common/Loader/LoaderProvider";
import { BackIcon } from "../Icons/BackIcon";
import CutOffText from "../common/CutOffText";
import { MAX_OPTION_LENGTH, OPTION_STAT_LABEL_MAX_LENGTH } from "../../utils/validation";

const PREDICTION_DATETIME_FORMAT = "MMM DD, YYYY - hh:mm A";

const PredictionDetails = ({ predictionId, onBack }) => {
  const dispatch = useDispatch();
  const [prediction, setPrediction] = useState(null);
  const [optionStats, setOptionStats] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [participantCount, setParticipantCount] = useState(0);
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    const fetchDetails = async () => {
      showLoader();
      try {
        const res = await dispatch(getPredictionById(predictionId));
        if (res.status) {
          const data = res.data?.data;
          setPrediction(data?.prediction);
          setOptionStats(data?.option_stats || []);
          setParticipants(data?.participants || []);
          setParticipantCount(data?.participant_count || 0);
        }
      } finally {
        hideLoader();
      }
    };
    fetchDetails();
    // eslint-disable-next-line
  }, [predictionId]);

  const getStatusColor = (status) => {
    return status === "published" ? "green" : "orange";
  };

  const participantColumns = [
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <span>{record.user?.name || `${record.user?.first_name || ""} ${record.user?.last_name || ""}`}</span>
        </div>
      ),
    },
    {
      title: "Email",
      key: "email",
      render: (_, record) => record.user?.email || "-",
    },
    {
      title: "Selected Option",
      key: "selected_option",
      render: (_, record) => {
        const option = prediction?.options?.find(
          (o) => o._id === record.selected_option_id
        );
        return option ? (
          <Tag color="blue">{option.label}</Tag>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Participated At",
      key: "participated_at",
      render: (_, record) =>
        record.participated_at
          ? dayjs(record.participated_at).format(PREDICTION_DATETIME_FORMAT)
          : "-",
    },
  ];

  if (!prediction) return null;

  const isExpired = dayjs(prediction.end_date).isBefore(dayjs());

  return (
    <div>
      <div className="flex items-center mb-6">
        <span className="cursor-pointer mr-3" onClick={onBack}>
          <BackIcon />
        </span>
        <h2 className="text-2xl font-semibold font-poppins m-0">
          Prediction Details
        </h2>
      </div>

      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
          <Descriptions.Item label="Question" span={2}>
            <span className="font-semibold">{prediction.question}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Subtitle" span={2}>
            {prediction.subtitle || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(prediction.status)}>
              {prediction.status?.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="End Date">
            <span className={isExpired ? "text-red-500" : ""}>
              {dayjs(prediction.end_date).format(PREDICTION_DATETIME_FORMAT)}
              {isExpired && " (Expired)"}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Tags" span={2}>
            {prediction.tags?.length > 0
              ? prediction.tags.map((tag) => (
                  <Tag key={tag} color="blue" className="mb-1">
                    {tag}
                  </Tag>
                ))
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Published At">
            {prediction.published_at
              ? dayjs(prediction.published_at).format(PREDICTION_DATETIME_FORMAT)
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Total Participants" span={2}>
            <span className="font-semibold text-lg">{participantCount}</span>
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* Option Stats */}
      {optionStats.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Option Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {optionStats.map((stat) => (
              <div
                key={stat.option_id}
                className="border rounded-lg p-4 bg-white min-w-0"
              >
                <div className="flex justify-between mb-2 gap-2 min-w-0">
                  <span className="font-medium min-w-0 flex-1">
                    <CutOffText maxLength={OPTION_STAT_LABEL_MAX_LENGTH} value={stat.label} />
                  </span>
                  <span className="text-gray-500 shrink-0 whitespace-nowrap">
                    {stat.count} votes ({stat.percentage}%)
                  </span>
                </div>
                <Progress
                  percent={parseFloat(stat.percentage)}
                  strokeColor="#4f46e5"
                  showInfo={false}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Options List (for drafts with no stats) */}
      {optionStats.length === 0 && prediction.options?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {prediction.options.map((opt, index) => (
              <div
                key={opt._id || index}
                className="border rounded-lg p-3 bg-white flex items-center gap-3"
              >
                <span className="w-7 h-7 rounded-full bg-[#4f46e5] text-white flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <CutOffText
                    maxLength={MAX_OPTION_LENGTH}
                    value={opt.label || opt}
                  />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Participants Table */}
      {participants.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Participants</h3>
          <Table
            dataSource={participants}
            columns={participantColumns}
            pagination={{ pageSize: 10 }}
            rowKey={(record) =>
              record.user?._id || Math.random().toString()
            }
            scroll={{ x: 800 }}
          />
        </div>
      )}
    </div>
  );
};

export default PredictionDetails;
